// src/pages/mlb/PitcherData.jsx
import React, { useEffect, useMemo, useState } from "react";
/* ============================ CONFIG ============================ */
const DATA_URL = "/data/mlb/latest/pitcher_data.json";
const SITE_ID_SOURCE =
  import.meta.env?.VITE_MLB_SITE_ID_URL || "/data/mlb/latest/site_ids.json";
const TITLE = "MLB — Pitcher Data";
const LOGO_BASE = "/logos/mlb";
const LOGO_EXT = "png";
/* ============================ HELPERS ============================ */
const norm = (v) => (v == null ? "" : String(v).trim());
const lower = (s) => norm(s).toLowerCase();
const squish = (s) =>
  lower(s).replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
const num = (v) => {
  if (v == null || v === "") return null;
  const s = String(v).trim();
  const clean = s.replace(/[,\s$]/g, "").replace(/%$/, "");
  const n = Number(clean);
  return Number.isFinite(n) ? n : null;
};
const keynorm = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9 ]/g, "");
function fmtPct1(v) {
  if (v == null || v === "") return "";
  let s = String(v).trim();
  let hadPercent = false;
  if (s.endsWith("%")) {
    hadPercent = true;
    s = s.slice(0, -1);
  }
  let n = num(s);
  if (n == null) return "";
  if (!hadPercent && Math.abs(n) <= 1) n *= 100;
  return `${n.toFixed(1)}%`;
}
function fmtIndexPct(v) {
  if (v == null || v === "") return "";
  const raw = String(v).trim();
  if (raw.endsWith("%")) {
    const n = num(raw.slice(0, -1));
    if (n == null) return "";
    const out = Math.abs(n) <= 2 ? n * 100 : n;
    return `${out.toFixed(1)}%`;
  }
  const n = num(raw);
  if (n == null) return "";
  const out = Math.abs(n) <= 2 ? n * 100 : n;
  return `${out.toFixed(1)}%`;
}
const fmt1 = (v) => {
  const n = num(v);
  return n == null ? "" : n.toFixed(1).replace(/\.0$/, "");
};
const fmt3 = (v) => {
  const n = num(v);
  return n == null ? "" : n.toFixed(3);
};
const fmtMoney0 = (v) => {
  const n = num(v);
  return n == null ? "" : `$${n.toLocaleString()}`;
};
function time12(s) {
  const v = norm(s);
  if (!v) return "";
  if (/\d{1,2}:\d{2}(:\d{2})?\s?[AP]M/i.test(v))
    return v.toUpperCase().replace(/\s+/g, " ");
  const m = v.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) return v;
  let hh = Number(m[1]);
  const mm = m[2];
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = ((hh + 11) % 12) + 1;
  return `${hh}:${mm} ${ampm}`;
}
function TeamWithLogo({ code }) {
  const abv = String(code || "").toUpperCase();
  if (!abv) return null;
  const src = `${LOGO_BASE}/${abv}.${LOGO_EXT}`;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <img
        alt={`${abv} logo`}
        src={src}
        className="h-4 w-4 shrink-0"
        onError={(e) => (e.currentTarget.style.display = "none")}
      />
      <span className="truncate max-w-[60px]">{abv}</span>
    </span>
  );
}
/* ---------------------- site id / slate helpers -------------------- */
function buildSiteIdIndex(raw) {
  const out = {
    dk: new Map(),
    fd: new Map(),
  };
  if (!raw || typeof raw !== "object") return out;
  for (const siteKey of ["dk", "fd"]) {
    const arr = Array.isArray(raw?.[siteKey]) ? raw[siteKey] : [];
    for (const row of arr) {
      const nameKey = keynorm(row?.name);
      const teamKey = String(row?.team || "").toUpperCase().trim();
      const comboKey = `${nameKey}__${teamKey}`;
      out[siteKey].set(comboKey, {
        id: row?.id ?? null,
        salary: num(row?.salary),
        early_id: row?.early_id ?? null,
        early_salary: num(row?.early_salary),
        late_id: row?.late_id ?? null,
        late_salary: num(row?.late_salary),
        pos: row?.pos ?? null,
        pos_primary: row?.pos_primary ?? null,
        team: row?.team ?? null,
        opp: row?.opp ?? null,
        name: row?.name ?? null,
      });
    }
  }
  return out;
}
function getSiteIdEntry(siteIdIndex, row, site, playerNameKey, teamKey) {
  const siteKey = site === "fd" ? "fd" : "dk";
  const nameKey = keynorm(playerNameKey ? row?.[playerNameKey] : "");
  const teamVal = String(teamKey ? row?.[teamKey] : "").toUpperCase().trim();
  const comboKey = `${nameKey}__${teamVal}`;
  return siteIdIndex?.[siteKey]?.get(comboKey) ?? null;
}
function getSlateSalary(entry, slate) {
  if (!entry) return null;
  if (slate === "early") return num(entry.early_salary);
  if (slate === "late") return num(entry.late_salary);
  return num(entry.salary);
}
function hasSlateEntry(entry, slate) {
  if (!entry) return false;
  if (slate === "early") {
    return !!entry.early_id && num(entry.early_salary) !== null;
  }
  if (slate === "late") {
    return !!entry.late_id && num(entry.late_salary) !== null;
  }
  if (slate === "main") {
    const sal = num(entry.salary);
    return !!entry.id && sal !== null && sal > 0 && sal < 50000;
  }
  if (slate === "all") {
    return (
      (!!entry.id && num(entry.salary) !== null && num(entry.salary) > 0) ||
      (!!entry.early_id &&
        num(entry.early_salary) !== null &&
        num(entry.early_salary) > 0) ||
      (!!entry.late_id &&
        num(entry.late_salary) !== null &&
        num(entry.late_salary) > 0)
    );
  }
  return true;
}
function rowMatchesSlate(
  siteIdIndex,
  row,
  site,
  slate,
  playerNameKey,
  teamKey
) {
  if (slate === "all") return true;
  if (site === "dk" || site === "fd") {
    const entry = getSiteIdEntry(siteIdIndex, row, site, playerNameKey, teamKey);
    return hasSlateEntry(entry, slate);
  }
  const dkEntry = getSiteIdEntry(siteIdIndex, row, "dk", playerNameKey, teamKey);
  const fdEntry = getSiteIdEntry(siteIdIndex, row, "fd", playerNameKey, teamKey);
  return hasSlateEntry(dkEntry, slate) || hasSlateEntry(fdEntry, slate);
}
function getRenderedSalary(
  siteIdIndex,
  row,
  site,
  slate,
  playerNameKey,
  teamKey,
  baseValue
) {
  if (slate === "all") return num(baseValue);
  const entry = getSiteIdEntry(siteIdIndex, row, site, playerNameKey, teamKey);
  return getSlateSalary(entry, slate);
}
/* ---------------------- last-updated via meta.json -------------------- */
function toMetaUrl(urlLike) {
  return String(urlLike || "").replace(/[^/]+$/, "meta.json");
}
function parseMetaToDate(meta) {
  const iso =
    meta?.updated_iso ||
    meta?.updated_utc ||
    meta?.source_mtime_iso ||
    meta?.last_updated ||
    meta?.timestamp;
  if (iso) {
    const d = new Date(iso);
    if (!isNaN(d)) return d;
  }
  const epoch = meta?.updated_epoch ?? meta?.epoch;
  if (Number.isFinite(epoch)) {
    const d = new Date(epoch * (epoch > 10_000_000_000 ? 1 : 1000));
    if (!isNaN(d)) return d;
  }
  return null;
}
function useLastUpdatedFromSource(sourceUrl) {
  const metaUrl = useMemo(() => toMetaUrl(sourceUrl), [sourceUrl]);
  const [text, setText] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${metaUrl}?_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!r.ok) return;
        const meta = await r.json();
        const d = parseMetaToDate(meta);
        if (!d) return;
        const t = d.toLocaleString(undefined, {
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
        if (alive) setText(t);
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, [metaUrl]);
  return text;
}
/* ===================== DISPLAY ORDER & HEADER BANDS ===================== */
const COLS = [
  { id: "hand", label: "Hand", keys: ["Hand", "Throws", "Handedness"] },
  { id: "player", label: "Player", keys: ["player", "Player", "Name"] },
  { id: "dk", label: "DK", keys: ["DK", "DK Sal", "DK Salary"] },
  { id: "fd", label: "FD", keys: ["FD", "FD Sal", "FD Salary"] },
  { id: "team", label: "Team", keys: ["Team", "Tm"] },
  { id: "opp", label: "Opp", keys: ["Opp", "OPP", "Opponent"] },
  { id: "park", label: "Park", keys: ["Park", "Ballpark"] },
  { id: "time", label: "Time", keys: ["Time", "Start", "Start Time"] },
  { id: "total", label: "Total", keys: ["Total", "O/U", "Team Total", "TT"] },
  { id: "winpct", label: "W%", keys: ["W%", "Win%"] },
  { id: "kline", label: "K", keys: ["K", "Kline", "Ks"] },
  { id: "field", label: "Field", keys: ["Field", "Field%"] },
  {
    id: "rating",
    label: "Rating",
    keys: ["Rating", "Rate", "Park Rating", "Ballpark Rating", "Park Factor", "PF"],
  },
  { id: "opp_kpct", label: "K%", keys: ["opp K%", "Opp K%"] },
  { id: "opp_bbpct", label: "BB%", keys: ["opp BB%", "Opp BB%"] },
  { id: "opp_woba", label: "wOBA", keys: ["opp wOBA", "Opp wOBA"] },
  { id: "opp_iso", label: "ISO", keys: ["opp ISO", "Opp ISO"] },
  { id: "opp_wrcplus", label: "wRC+", keys: ["opp wRC+", "Opp wRC+"] },
  { id: "ip", label: "IP", keys: ["IP", "IP/G"] },
  { id: "velo", label: "Velo", keys: ["Velo", "FB Velo", "Velocity"] },
  { id: "xfip", label: "xFIP", keys: ["xFIP", "xfip"] },
  { id: "xwoba", label: "xwOBA", keys: ["xWOBA", "xwOBA"] },
  { id: "xiso", label: "xISO", keys: ["xISO"] },
  { id: "xslg", label: "xSLG", keys: ["xSLG"] },
  { id: "ops", label: "OPS", keys: ["OPS"] },
  { id: "p_kpct", label: "K%", keys: ["K%", "K% (P)", "Pitch K%", "K%_P"] },
  { id: "p_bbpct", label: "BB%", keys: ["BB%", "BB% (P)", "Pitch BB%", "BB%_P"] },
  { id: "k9", label: "K/9", keys: ["K/9", "K9"] },
  { id: "bb9", label: "BB/9", keys: ["BB/9", "BB9"] },
  { id: "hr9", label: "HR/9", keys: ["HR/9", "HR9"] },
  { id: "gbpct", label: "GB%", keys: ["GB%", "GB% (P)"] },
  { id: "fbpct", label: "FB%", keys: ["FB%", "FB% (P)"] },
  { id: "hhpct", label: "HH%", keys: ["HH%", "HardHit%", "Hard%"] },
  { id: "barpct", label: "Bar%", keys: ["Bar%", "Barrel%"] },
  { id: "ev", label: "EV", keys: ["EV", "Avg EV", "Exit Velo"] },
  { id: "la", label: "LA", keys: ["LA", "Launch Angle"] },
  { id: "spin", label: "Spin", keys: ["Spin", "Spin Rate"] },
  { id: "dk_proj", label: "DK Proj", keys: ["DK_1"] },
  { id: "dk_val", label: "DK Val", keys: ["Val"] },
  { id: "fd_proj", label: "FD Proj", keys: ["FD_1"] },
  { id: "fd_val", label: "FD Val", keys: ["Val_1"] },
];
const BANDS = [
  ["PLAYER INFO", ["hand", "player", "dk", "fd"]],
  ["MATCHUP INFO", ["team", "opp", "park", "time"]],
  ["VEGAS", ["total", "winpct", "kline", "field", "rating"]],
  [
    "OPPONENT SPLITS VS HANDEDNESS",
    ["opp_kpct", "opp_bbpct", "opp_woba", "opp_iso", "opp_wrcplus"],
  ],
  [
    "ADVANCED STATS",
    ["ip", "velo", "xfip", "xwoba", "xiso", "xslg", "ops", "p_kpct", "p_bbpct"],
  ],
  ["RATIOS", ["k9", "bb9", "hr9"]],
  ["STATCAST", ["gbpct", "fbpct", "hhpct", "barpct", "ev", "la", "spin"]],
  ["PROJECTIONS", ["dk_proj", "dk_val", "fd_proj", "fd_val"]],
];
const PCT_IDS = new Set([
  "winpct",
  "opp_kpct",
  "opp_bbpct",
  "p_kpct",
  "p_bbpct",
  "gbpct",
  "fbpct",
  "hhpct",
  "barpct",
]);
const THREE_DECIMAL_IDS = new Set([
  "opp_woba",
  "opp_iso",
  "xwoba",
  "xiso",
  "xslg",
  "ops",
]);
const THICK_LEFT_OF = new Set([
  "team",
  "total",
  "opp_kpct",
  "ip",
  "k9",
  "gbpct",
  "dk_proj",
]);
/* ----------------- conditional formatting ----------------- */
const HIGHER_IS_BETTER = new Set([
  "winpct",
  "kline",
  "rating",
  "opp_kpct",
  "ip",
  "velo",
  "p_kpct",
  "k9",
  "gbpct",
  "dk_proj",
  "dk_val",
  "fd_proj",
  "fd_val",
]);
const LOWER_IS_BETTER = new Set([
  "dk",
  "fd",
  "total",
  "opp_bbpct",
  "opp_woba",
  "opp_iso",
  "opp_wrcplus",
  "xfip",
  "xwoba",
  "xiso",
  "xslg",
  "ops",
  "p_bbpct",
  "bb9",
  "hr9",
  "fbpct",
  "hhpct",
  "barpct",
  "ev",
]);
const SAMPLE_SIZE_IDS = new Set([
  "p_kpct",
  "p_bbpct",
  "gbpct",
  "fbpct",
  "hhpct",
  "barpct",
]);
function getSampleSizeForId(id, row, idToKey) {
  const ipKey = idToKey.get("ip");
  const ip = num(ipKey ? row[ipKey] : null);
  if (ip != null) return ip * 4.25;
  return null;
}
function shrinkToMean(value, mean, sample, stabilizeAt = 150) {
  if (
    value == null ||
    mean == null ||
    sample == null ||
    !Number.isFinite(value) ||
    !Number.isFinite(mean) ||
    !Number.isFinite(sample)
  ) {
    return value;
  }
  const w = Math.max(0, Math.min(1, sample / stabilizeAt));
  return mean * (1 - w) + value * w;
}
function numericFromRaw(id, raw) {
  if (raw == null || raw === "") return null;
  let v = String(raw).trim();
  if (id === "rating") {
    if (v.endsWith("%")) {
      const n = num(v.slice(0, -1));
      if (n == null) return null;
      return Math.abs(n) <= 2 ? n * 100 : n;
    }
    const n = num(v);
    if (n == null) return null;
    return Math.abs(n) <= 2 ? n * 100 : n;
  }
  if (v.endsWith("%")) v = v.slice(0, -1);
  let n = num(v);
  if (n == null) return null;
  if (PCT_IDS.has(id) && Math.abs(n) <= 1) n *= 100;
  return n;
}
function computeStats(rows, ids, idToKey) {
  const out = {};
  const means = {};
  for (const id of ids) {
    const key = idToKey.get(id);
    if (!key || !SAMPLE_SIZE_IDS.has(id)) continue;
    let sum = 0;
    let count = 0;
    for (const r of rows) {
      const n = numericFromRaw(id, r[key]);
      if (n == null || !Number.isFinite(n)) continue;
      sum += n;
      count += 1;
    }
    if (count > 0) means[id] = sum / count;
  }
  const percentile = (sortedVals, p) => {
    if (!sortedVals.length) return null;
    if (sortedVals.length === 1) return sortedVals[0];
    const idx = (sortedVals.length - 1) * p;
    const lo = Math.floor(idx);
    const hi = Math.ceil(idx);
    if (lo === hi) return sortedVals[lo];
    const w = idx - lo;
    return sortedVals[lo] * (1 - w) + sortedVals[hi] * w;
  };
  for (const id of ids) {
    if (!HIGHER_IS_BETTER.has(id) && !LOWER_IS_BETTER.has(id)) continue;
    const key = idToKey.get(id);
    if (!key) continue;
    const values = [];
    for (const r of rows) {
      let n = numericFromRaw(id, r[key]);
      if (n == null || !Number.isFinite(n)) continue;
      if (SAMPLE_SIZE_IDS.has(id)) {
        const sample = getSampleSizeForId(id, r, idToKey);
        n = shrinkToMean(n, means[id], sample, 150);
      }
      if (n != null && Number.isFinite(n)) values.push(n);
    }
    if (values.length < 2) continue;
    values.sort((a, b) => a - b);
    const min = values[0];
    const max = values[values.length - 1];
    if (min !== max) {
      out[id] = {
        min,
        max,
        median: percentile(values, 0.5),
        p15: percentile(values, 0.15),
        p85: percentile(values, 0.85),
        mean: means[id] ?? null,
      };
    }
  }
  return out;
}
function colorFor(id, raw, stats, isDark, row, idToKey) {
  const higher = HIGHER_IS_BETTER.has(id);
  const lowerDir = LOWER_IS_BETTER.has(id);
  if (!higher && !lowerDir) return null;
  const st = stats[id];
  if (!st) return null;
  let n = numericFromRaw(id, raw);
  if (n == null || !Number.isFinite(n)) return null;
  if (SAMPLE_SIZE_IDS.has(id)) {
    const sample = getSampleSizeForId(id, row, idToKey);
    n = shrinkToMean(n, st.mean, sample, 150);
  }
  if (n == null || !Number.isFinite(n)) return null;
  const isOpponentSplit = new Set([
    "opp_kpct",
    "opp_bbpct",
    "opp_woba",
    "opp_iso",
    "opp_wrcplus",
  ]).has(id);
  let t;
  if (
    isOpponentSplit &&
    st.median != null &&
    st.p15 != null &&
    st.p85 != null &&
    st.p15 < st.median &&
    st.median < st.p85
  ) {
    if (n <= st.median) {
      t = 0.5 * ((n - st.p15) / (st.median - st.p15));
    } else {
      t = 0.5 + 0.5 * ((n - st.median) / (st.p85 - st.median));
    }
    t = Math.max(0, Math.min(1, t));
  } else {
    t = (n - st.min) / (st.max - st.min);
    t = Math.max(0, Math.min(1, t));
  }
  if (lowerDir && !higher) t = 1 - t;
  let h, s, l;
  if (isDark) {
    if (t < 0.5) {
      const u = t / 0.5;
      h = 8 + u * 42;
      s = 90;
      l = 70 - u * 8;
    } else {
      const u = (t - 0.5) / 0.5;
      h = 50 + u * 70;
      s = 88;
      l = 62 + u * 8;
    }
  } else {
    if (t < 0.5) {
      const u = t / 0.5;
      h = 8 + u * 42;
      s = 85;
      l = 48 - u * 4;
    } else {
      const u = (t - 0.5) / 0.5;
      h = 50 + u * 70;
      s = 80;
      l = 44 - u * 4;
    }
  }
  return `hsl(${h} ${s}% ${l}%)`;
}
/* ============================== DATA FETCH ============================== */
function useJson(url) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (alive) setRows(Array.isArray(j) ? j : j?.data || j?.rows || []);
      } catch (e) {
        if (alive) setErr(String(e.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return { rows, loading, err };
}
/* ============================== MAIN PAGE ============================== */
export default function PitcherData() {
  const { rows, loading, err } = useJson(DATA_URL);
  const updatedText = useLastUpdatedFromSource(DATA_URL);
  const [siteIdData, setSiteIdData] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${SITE_ID_SOURCE}?_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!r.ok) return;
        const j = await r.json();
        if (alive) setSiteIdData(j);
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, []);
  const siteIdIndex = useMemo(() => buildSiteIdIndex(siteIdData), [siteIdData]);
  const rawCols = useMemo(() => (rows[0] ? Object.keys(rows[0]) : []), [rows]);
  const rawLower = useMemo(() => rawCols.map(squish), [rawCols]);
  const colById = useMemo(() => {
    const m = new Map();
    for (const c of COLS) m.set(c.id, c);
    return m;
  }, []);
  const findKey = (aliases, fuzzy) => {
    for (const alias of aliases) {
      const idx = rawLower.findIndex((lc) => lc === squish(alias));
      if (idx !== -1) return rawCols[idx];
    }
    if (fuzzy) {
      const idx = rawLower.findIndex(fuzzy);
      if (idx !== -1) return rawCols[idx];
    }
    return null;
  };
  const idToKey = useMemo(() => {
    const m = new Map();
    for (const c of COLS) {
      let fuzzy = null;
      if (c.id === "hand") {
        fuzzy = (lc) =>
          lc.includes("throw") || lc.includes("handedness") || lc === "hand";
      }
      if (c.id === "winpct") {
        fuzzy = (lc) => lc === "w%" || lc === "win%" || lc.includes("win");
      }
      if (c.id === "kline") {
        fuzzy = (lc) => lc === "k" || lc.includes("kline") || lc === "ks";
      }
      if (c.id === "rating") {
        fuzzy = (lc) =>
          (lc.includes("rating") || lc.includes("factor") || lc === "pf") &&
          !lc.includes("park");
      }
      if (c.id === "opp_kpct") {
        fuzzy = (lc) =>
          lc.includes("k") &&
          (lc.includes("opp") || lc.includes("vs hand") || lc.includes("team"));
      }
      if (c.id === "opp_bbpct") {
        fuzzy = (lc) =>
          lc.includes("bb") &&
          (lc.includes("opp") || lc.includes("vs hand") || lc.includes("team"));
      }
      if (c.id === "p_kpct") {
        fuzzy = (lc) =>
          lc.includes("k") &&
          (lc.includes("(p)") || lc.includes("pitch") || lc.includes("_p"));
      }
      if (c.id === "p_bbpct") {
        fuzzy = (lc) =>
          lc.includes("bb") &&
          (lc.includes("(p)") || lc.includes("pitch") || lc.includes("_p"));
      }
      if (c.id === "sws") {
        fuzzy = (lc) => lc.includes("sws") || lc.includes("swstr");
      }
      if (c.id === "gbpct") {
        fuzzy = (lc) => lc.includes("gb");
      }
      if (c.id === "fbpct") {
        fuzzy = (lc) => lc.includes("fb");
      }
      if (c.id === "hhpct") {
        fuzzy = (lc) => lc.includes("hard") || lc.includes("hh%");
      }
      if (c.id === "barpct") {
        fuzzy = (lc) => lc.includes("bar");
      }
      if (c.id === "ev") {
        fuzzy = (lc) => lc.includes("exit") || lc.includes("velo");
      }
      m.set(c.id, findKey(c.keys, fuzzy));
    }
    return m;
  }, [rawCols, rawLower]);
  const [q, setQ] = useState("");
  const [theme, setTheme] = useState("dark");
  const [site, setSite] = useState("both"); // dk | fd | both
  const [slate, setSlate] = useState("all"); // all | early | main | late
  const isDark = theme === "dark";
  const playerKey = idToKey.get("player");
  const teamKey = idToKey.get("team");
  const oppKey = idToKey.get("opp");
  const dkKey = idToKey.get("dk");
  const fdKey = idToKey.get("fd");
  const filtered = useMemo(() => {
    const s = lower(q);
    return rows.filter((r) => {
      if (
        playerKey &&
        teamKey &&
        !rowMatchesSlate(siteIdIndex, r, site, slate, playerKey, teamKey)
      ) {
        return false;
      }
      if (!s) return true;
      const name = lower(playerKey ? r[playerKey] : "");
      const t = lower(teamKey ? r[teamKey] : "");
      const o = lower(oppKey ? r[oppKey] : "");
      return name.includes(s) || t.includes(s) || o.includes(s);
    });
  }, [rows, q, siteIdIndex, site, slate, playerKey, teamKey, oppKey]);
  const [sort, setSort] = useState({
    key: dkKey || playerKey || "",
    dir: dkKey ? "desc" : "asc",
  });
  useEffect(() => {
    const dk = idToKey.get("dk");
    const pl = idToKey.get("player");
    setSort({ key: dk || pl || "", dir: dk ? "desc" : "asc" });
  }, [idToKey]);
  const onSort = (id) => {
    const k = idToKey.get(id);
    if (!k) return;
    const defaultDir =
      HIGHER_IS_BETTER.has(id) || LOWER_IS_BETTER.has(id) || PCT_IDS.has(id)
        ? "desc"
        : "asc";
    setSort((prev) =>
      prev.key === k
        ? { key: k, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key: k, dir: defaultDir }
    );
  };
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const k = sort.key;
    const dir = sort.dir === "asc" ? 1 : -1;
    if (!k) return arr;
    arr.sort((a, b) => {
      if (k === dkKey && playerKey && teamKey) {
        const av = getRenderedSalary(
          siteIdIndex,
          a,
          "dk",
          slate,
          playerKey,
          teamKey,
          a[dkKey]
        );
        const bv = getRenderedSalary(
          siteIdIndex,
          b,
          "dk",
          slate,
          playerKey,
          teamKey,
          b[dkKey]
        );
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return dir * (av - bv);
      }
      if (k === fdKey && playerKey && teamKey) {
        const av = getRenderedSalary(
          siteIdIndex,
          a,
          "fd",
          slate,
          playerKey,
          teamKey,
          a[fdKey]
        );
        const bv = getRenderedSalary(
          siteIdIndex,
          b,
          "fd",
          slate,
          playerKey,
          teamKey,
          b[fdKey]
        );
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return dir * (av - bv);
      }
      const av = a[k];
      const bv = b[k];
      const an = num(av);
      const bn = num(bv);
      if (an != null && bn != null) return dir * (an - bn);
      return (
        dir *
        String(av ?? "").localeCompare(String(bv ?? ""), undefined, {
          sensitivity: "base",
        })
      );
    });
    return arr;
  }, [filtered, sort, siteIdIndex, slate, playerKey, teamKey, dkKey, fdKey]);
  const flatIds = useMemo(() => BANDS.flatMap(([, ids]) => ids), []);
  const stats = useMemo(
    () => computeStats(filtered, flatIds, idToKey),
    [filtered, flatIds, idToKey]
  );
  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";
  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm overflow-hidden"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden";
  const headerBandClass =
    "text-[11px] font-bold uppercase " +
    (isDark ? "bg-slate-900 text-slate-200" : "bg-blue-100 text-gray-700");
  const headerRowClass = isDark ? "bg-slate-800" : "bg-blue-50";
  const headerCls =
    "px-2 py-1 font-semibold text-center text-[11px] whitespace-nowrap cursor-pointer select-none";
  const cellBase =
    "px-2 py-0 text-center text-[12px] whitespace-nowrap overflow-hidden text-ellipsis";
  const cellTextColor = isDark ? "text-slate-100" : "text-gray-900";
  const cellCls = `${cellBase} ${cellTextColor}`;
  const headerSolid = isDark ? "#0f172a" : "#f9fafb";
  const getDividerClasses = (id) => {
    const thickLeft = THICK_LEFT_OF.has(id);
    return thickLeft
      ? "border-l-2 border-slate-700/80 border-r border-slate-700/50"
      : "border-r border-slate-700/50";
  };
  const renderVal = (id, key, row) => {
    const raw = key ? row[key] : "";
    if (id === "time") return time12(raw);
    if (id === "team" || id === "opp") return <TeamWithLogo code={raw} />;
    if (THREE_DECIMAL_IDS.has(id)) return fmt3(raw);
    if (id === "rating") return fmtIndexPct(raw);
    if (PCT_IDS.has(id)) return fmtPct1(raw);
    if (id === "dk" && playerKey && teamKey && key) {
      return fmtMoney0(
        getRenderedSalary(siteIdIndex, row, "dk", slate, playerKey, teamKey, row[key])
      );
    }
    if (id === "fd" && playerKey && teamKey && key) {
      return fmtMoney0(
        getRenderedSalary(siteIdIndex, row, "fd", slate, playerKey, teamKey, row[key])
      );
    }
    const n = num(raw);
    return n == null ? String(raw ?? "") : fmt1(n);
  };
  return (
    <div className={outerClass}>
      <div className="w-full px-4 md:px-6 py-5">
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-xl font-bold tracking-tight">{TITLE}</h1>
            <span className={"text-xs font-normal italic " + (isDark ? "text-slate-100" : "text-slate-700")}>Updated: {updatedText || "—"}</span>
          </div>
            <div
              className={
                "inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
                (isDark
                  ? "bg-slate-900/90 ring-slate-700/80"
                  : "bg-white ring-slate-300")
              }
            >
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (isDark
                    ? "bg-sky-500 text-slate-950 shadow-sm"
                    : "text-slate-500 hover:text-slate-800")
                }
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (!isDark
                    ? "bg-sky-500 text-slate-950 shadow-sm"
                    : "text-slate-400 hover:text-slate-100")
                }
              >
                Light
              </button>
            </div>
        </header>
        <section aria-label="Site and slate" className={cardClass + " mb-4"}>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3">
            <div
              className={
                "inline-flex flex-wrap items-center gap-1 border-b " +
                (isDark
                  ? "border-slate-800"
                  : "border-slate-200")
              }
            >
              {[
                { k: "dk", label: "DK", logo: "/logos/dk.png" },
                { k: "fd", label: "FD", logo: "/logos/fd.png" },
                { k: "both", label: "Both" },
              ].map((x) => (
                <button
                  key={x.k}
                  onClick={() => setSite(x.k)}
                  className={`h-9 px-3 border-b-2 text-xs font-semibold inline-flex items-center gap-1.5 transition ${
                    site === x.k
                      ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                      : isDark
                      ? "border-transparent text-slate-400 hover:text-white"
                      : "border-transparent text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {x.logo ? (
                    <img src={x.logo} alt={x.label} className="w-4 h-4" />
                  ) : null}
                  <span>{x.label}</span>
                </button>
              ))}
            </div>
            <div
              className={
                "inline-flex flex-wrap items-center gap-1 border-b " +
                (isDark
                  ? "border-slate-800"
                  : "border-slate-200")
              }
              title="Slate filter"
            >
              {[
                { k: "all", label: "All" },
                { k: "early", label: "Early" },
                { k: "main", label: "Main" },
                { k: "late", label: "Late" },
              ].map((x) => (
                <button
                  key={x.k}
                  onClick={() => setSlate(x.k)}
                  className={`h-9 px-3 border-b-2 text-xs font-semibold transition ${
                    slate === x.k
                      ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                      : isDark
                      ? "border-transparent text-slate-400 hover:text-white"
                      : "border-transparent text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {x.label}
                </button>
              ))}
            </div>
          </div>
        </section>
        {err && (
          <div
            className={
              "mb-2 font-semibold " + (isDark ? "text-red-400" : "text-red-600")
            }
          >
            Data load error: {err}
          </div>
        )}
        <section aria-label="Pitcher data" className={cardClass}>
          <div className={"flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 " + (isDark ? "border-slate-800" : "border-slate-200")}>
            <h2 className="text-sm font-semibold">Pitcher Data</h2>
            <button
              type="button"
              onClick={() => {
                setQ("");
                setTheme("dark");
                setSite("both");
                setSlate("all");
                const dk = idToKey.get("dk");
                const pl = idToKey.get("player");
                setSort({ key: dk || pl || "", dir: dk ? "desc" : "asc" });
              }}
              className={
                "h-8 rounded-md border px-3 text-xs " +
                (isDark
                  ? "bg-slate-950/80 border-slate-700 text-slate-100"
                  : "bg-white border-gray-300 text-gray-900")
              }
            >
              Reset
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 px-4 py-3">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search pitcher / team / opp…"
              className={
                "h-8 w-full sm:ml-auto sm:w-72 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                (isDark
                  ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                  : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400")
              }
            />
          </div>
          <div className="overflow-x-auto">
            <div
              className={
                sorted.length > 18 ? "max-h-[70vh] overflow-y-auto" : "overflow-y-visible"
              }
            >
              <table
                className="w-full border-separate text-[12px]"
                style={{ borderSpacing: 0 }}
              >
                <thead
                  className="sticky top-0 z-40"
                  style={{ backgroundColor: headerSolid }}
                >
                  <tr
                    className={headerBandClass}
                    style={{ backgroundColor: headerSolid }}
                  >
                    {BANDS.map(([name, ids]) => (
                      <th
                        key={name}
                        colSpan={ids.length}
                        className="px-2 py-1 text-center border-b border-slate-700/60"
                        style={{ backgroundColor: headerSolid }}
                      >
                        <span className="whitespace-nowrap">{name}</span>
                      </th>
                    ))}
                  </tr>
                  <tr className={headerRowClass}>
                    {flatIds.map((id) => {
                      const col = colById.get(id);
                      const key = idToKey.get(id);
                      const isSorted = key && key === sort.key;
                      const isPlayer = id === "player";
                      return (
                        <th
                          key={id}
                          className={`${headerCls} ${
                            isDark ? "text-slate-200" : "text-gray-700"
                          } ${getDividerClasses(id)} ${
                            isPlayer ? "sticky left-0 z-[60] shadow-[inset_-6px_0_6px_-6px_rgba(0,0,0,0.35)]" : ""
                          }`}
                          style={
                            isPlayer
                              ? {
                                  left: 0,
                                  backgroundColor: isDark ? "#1e293b" : "#eff6ff",
                                }
                              : undefined
                          }
                          onClick={() => onSort(id)}
                          title="Click to sort"
                        >
                          <span className="inline-flex items-center gap-1">
                            <span>{col?.label ?? id}</span>
                            {isSorted ? (
                              <span className={isDark ? "text-slate-400" : "text-gray-500"}>
                                {sort.dir === "desc" ? "▼" : "▲"}
                              </span>
                            ) : (
                              <span className={isDark ? "text-slate-600" : "text-gray-300"}>▲</span>
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        className={`${cellCls} ${
                          isDark ? "text-slate-400" : "text-gray-500"
                        } text-center`}
                        colSpan={flatIds.length}
                      >
                        Loading…
                      </td>
                    </tr>
                  ) : err ? (
                    <tr>
                      <td
                        className={`${cellCls} text-center ${
                          isDark ? "text-red-400" : "text-red-600"
                        }`}
                        colSpan={flatIds.length}
                      >
                        Failed to load: {err}
                      </td>
                    </tr>
                  ) : sorted.length === 0 ? (
                    <tr>
                      <td
                        className={`${cellCls} text-center ${
                          isDark ? "text-slate-500" : "text-gray-500"
                        }`}
                        colSpan={flatIds.length}
                      >
                        No rows match your filters.
                      </td>
                    </tr>
                  ) : (
                    sorted.map((row, rIdx) => {
                      const rowBgSolid =
                        rIdx % 2 === 0
                          ? isDark
                            ? "#020617"
                            : "#ffffff"
                          : isDark
                          ? "#0f172a"
                          : "#f9fafb";
                      const rowBg =
                        rIdx % 2 === 0
                          ? isDark
                            ? "bg-slate-950"
                            : "bg-white"
                          : isDark
                          ? "bg-slate-900"
                          : "bg-gray-50/40";
                      return (
                        <tr
                          key={rIdx}
                          className={
                            rowBg +
                            " " +
                            (isDark
                              ? "hover:bg-slate-900/80 transition-colors"
                              : "hover:bg-blue-50 transition-colors")
                          }
                        >
                          {flatIds.map((id) => {
                            const key = idToKey.get(id);
                            const isPlayer = id === "player";
                            let raw = key ? row[key] : null;
                            if (id === "dk" && key && playerKey && teamKey) {
                              raw = getRenderedSalary(
                                siteIdIndex,
                                row,
                                "dk",
                                slate,
                                playerKey,
                                teamKey,
                                row[key]
                              );
                            }
                            if (id === "fd" && key && playerKey && teamKey) {
                              raw = getRenderedSalary(
                                siteIdIndex,
                                row,
                                "fd",
                                slate,
                                playerKey,
                                teamKey,
                                row[key]
                              );
                            }
                            const textColor = colorFor(id, raw, stats, isDark, row, idToKey);
                            return (
                              <td
                                key={id + "-" + rIdx}
                                className={`${cellCls} ${
                                  isPlayer
                                    ? "text-left font-medium max-w-[220px] sticky left-0 z-[10] shadow-[inset_-6px_0_6px_-6px_rgba(0,0,0,0.35)]"
                                    : ""
                                } ${getDividerClasses(id)}`}
                                style={
                                  isPlayer
                                    ? {
                                        backgroundColor: rowBgSolid,
                                        left: 0,
                                      }
                                    : undefined
                                }
                                title={String(raw ?? "")}
                              >
                                {isPlayer ? (
                                  <span className="whitespace-nowrap overflow-hidden text-ellipsis inline-block max-w-[220px]">
                                    {renderVal(id, key, row)}
                                  </span>
                                ) : (
                                  <span
                                    style={textColor ? { color: textColor, fontWeight: 600 } : undefined}
                                  >
                                    {renderVal(id, key, row)}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}